// Shared presentational field helpers for the Manual Booking forms.
// Reuse the app's form CSS (inputFieldWrap / inputField) + MUI so the flight and
// hotel forms look identical to the rest of the admin (see PromoFormDialog).
import React from 'react';
import { Box, Typography, Checkbox, FormControlLabel, IconButton, Button } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

export const errorText: React.CSSProperties = {
  display: 'block',
  color: '#e31a24',
  fontSize: 12,
  marginTop: 4,
};

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  min?: string | number;
  readOnly?: boolean;
  disabled?: boolean;
}

export const Field: React.FC<FieldProps> = ({
  label,
  value,
  onChange,
  error,
  type = 'text',
  placeholder,
  required,
  min,
  readOnly,
  disabled,
}) => (
  <div className="form-control inputFieldWrap">
    <label>
      {label}
      {required && <span style={{ color: '#e31a24' }}> *</span>}
    </label>
    <input
      type={type}
      className="inputField"
      value={value}
      placeholder={placeholder}
      min={min as number}
      readOnly={readOnly}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={readOnly ? { backgroundColor: '#f7fcff', cursor: 'not-allowed' } : undefined}
    />
    {error && <span style={errorText}>{error}</span>}
  </div>
);

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
  error,
  required,
  disabled,
}) => (
  <div className="form-control inputFieldWrap">
    <label>
      {label}
      {required && <span style={{ color: '#e31a24' }}> *</span>}
    </label>
    <select
      className="inputField"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={disabled ? { backgroundColor: '#f7fcff', cursor: 'not-allowed' } : undefined}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    {error && <span style={errorText}>{error}</span>}
  </div>
);

interface CheckFieldProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

export const CheckField: React.FC<CheckFieldProps> = ({ label, checked, onChange }) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ color: '#272727', '&.Mui-checked': { color: '#272727' } }}
      />
    }
    label={label}
  />
);

// A titled, bordered card for a repeatable row (sector / passenger / hotel / guest),
// with an optional remove button in the header.
interface RowCardProps {
  title: string;
  onRemove?: () => void;
  removeDisabled?: boolean;
  children: React.ReactNode;
  tone?: 'default' | 'nested';
}

export const RowCard: React.FC<RowCardProps> = ({
  title,
  onRemove,
  removeDisabled,
  children,
  tone = 'default',
}) => (
  <Box
    sx={{
      border: '2px solid #e1e8ed',
      borderRadius: '8px',
      p: 2.5,
      mb: 2.5,
      backgroundColor: tone === 'nested' ? '#f9f9f9' : '#fff',
    }}
  >
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {onRemove && (
        <IconButton size="small" onClick={onRemove} disabled={removeDisabled} aria-label="remove">
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
    {children}
  </Box>
);

// "Add another …" button used under a list of repeatable rows.
export const AddButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Button
    variant="outlined"
    size="small"
    startIcon={<AddIcon />}
    onClick={onClick}
    sx={{
      color: '#272727',
      borderColor: '#272727',
      mb: 2,
      '&:hover': { borderColor: '#111', backgroundColor: '#f5f5f5' },
    }}
  >
    {label}
  </Button>
);

// Section heading inside a form. Renders an <h6> so it picks up the shared
// `.feeModalForm h6` underline-accent styling used across the admin (Flight
// Management, Super Admin, etc.).
export const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h6" sx={{ mt: 2 }}>
    {children}
  </Typography>
);

// Sub-heading inside a card (e.g. "Lead contact", "Fares (per passenger)").
// Extra space ABOVE separates it from the previous row; no space below so it
// couples with its own inputs (whose labels already carry a top margin).
export const SubTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ fontWeight: 700, mt: 2.5, mb: 0 }}>
    {children}
  </Typography>
);
